import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  LinearProgress,
  Stack
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  NetworkCheck as NetworkIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { LineChart } from './Charts';
import SystemHealthPanel from './SystemHealthPanel';
import AdminService from '../../services/AdminService';

const SystemMetrics = () => {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);

  const fetchMetrics = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const response = await AdminService.getSystemHealth();
      if (response && response.metrics) {
        setMetrics(response.metrics);
      }
      setError(null);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching system metrics:', err);
      setError('Failed to load system metrics. Please try again later.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    
    // Refresh metrics every 30 seconds
    const intervalId = setInterval(() => fetchMetrics(), 30000);
    
    return () => clearInterval(intervalId);
  }, []);

  const handleManualRefresh = () => {
    fetchMetrics(true);
  };

  const getCurrentMetrics = () => {
    if (metrics.length === 0) {
      return {
        cpu_usage: 0,
        memory_usage: 0,
        disk_usage: 0,
        network_latency: 0,
        uptime: '0d 0h 0m'
      };
    }
    
    const latest = metrics[metrics.length - 1];
    return {
      cpu_usage: parseFloat(latest?.cpu?.replace('%', '') || '0'),
      memory_usage: parseFloat(latest?.memory?.replace('%', '') || '0'),
      disk_usage: parseFloat(latest?.disk?.replace('%', '') || '0'),
      network_latency: parseFloat(latest?.latency?.replace('ms', '') || '0'),
      uptime: latest?.uptime || '0d 0h 0m'
    };
  };

  const getHealthStatus = () => {
    const current = getCurrentMetrics();
    const criticalThreshold = 90;
    const warningThreshold = 80;
    
    const maxUsage = Math.max(current.cpu_usage, current.memory_usage, current.disk_usage);
    
    if (maxUsage >= criticalThreshold) {
      return { status: 'Critical', color: 'error', icon: <ErrorIcon /> };
    } else if (maxUsage >= warningThreshold) {
      return { status: 'Warning', color: 'warning', icon: <WarningIcon /> };
    } else {
      return { status: 'Good', color: 'success', icon: <CheckCircleIcon /> };
    }
  };

  const MetricCard = ({ title, value, unit, icon, usage, color = 'primary' }) => (
    <Card sx={{ height: '100%', boxShadow: 3, borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={color}>
              {value}{unit}
            </Typography>
          </Box>
          <Box sx={{ color: `${color}.main`, opacity: 0.7 }}>
            {icon}
          </Box>
        </Stack>
        
        {usage !== undefined && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={usage}
              color={usage > 80 ? 'error' : usage > 60 ? 'warning' : 'success'}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {usage.toFixed(1)}% utilized
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const healthStatus = getHealthStatus();
  const currentMetrics = getCurrentMetrics();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            System Metrics
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Real-time monitoring and performance insights
          </Typography>
        </Box>
        
        <Box display="flex" alignItems="center" gap={2}>
          <Chip
            icon={healthStatus.icon}
            label={`System Health: ${healthStatus.status}`}
            color={healthStatus.color}
            variant="filled"
            sx={{ fontWeight: 'bold' }}
          />
          <Tooltip title="Refresh data">
            <IconButton
              onClick={handleManualRefresh}
              disabled={refreshing}
              sx={{ backgroundColor: 'white', boxShadow: 1 }}
            >
              {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Metric Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="CPU Usage"
            value={currentMetrics.cpu_usage.toFixed(1)}
            unit="%"
            icon={<SpeedIcon sx={{ fontSize: 32 }} />}
            usage={currentMetrics.cpu_usage}
            color="primary"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Memory Usage"
            value={currentMetrics.memory_usage.toFixed(1)}
            unit="%"
            icon={<MemoryIcon sx={{ fontSize: 32 }} />}
            usage={currentMetrics.memory_usage}
            color="secondary"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Disk Usage"
            value={currentMetrics.disk_usage.toFixed(1)}
            unit="%"
            icon={<StorageIcon sx={{ fontSize: 32 }} />}
            usage={currentMetrics.disk_usage}
            color="info"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Network Latency"
            value={currentMetrics.network_latency.toFixed(0)}
            unit="ms"
            icon={<NetworkIcon sx={{ fontSize: 32 }} />}
            color="success"
          />
        </Grid>

        {/* Chart Section */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" mb={3}>
                <TrendingUpIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Resource Usage History
                </Typography>
              </Box>
              <Box sx={{ height: 400, backgroundColor: '#fafafa', borderRadius: 1, p: 2 }}>
                <LineChart data={metrics} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* System Health Panel */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ boxShadow: 3, borderRadius: 2, height: 'fit-content' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                System Overview
              </Typography>
              
              <SystemHealthPanel 
                metrics={{
                  cpu_usage: `${currentMetrics.cpu_usage.toFixed(1)}%`,
                  memory_usage: `${currentMetrics.memory_usage.toFixed(1)}%`,
                  disk_usage: `${currentMetrics.disk_usage.toFixed(1)}%`,
                  network_latency: `${currentMetrics.network_latency.toFixed(0)}ms`,
                  uptime: currentMetrics.uptime,
                  last_refresh: lastRefresh.toLocaleString()
                }}
              />
              
              <Divider sx={{ my: 2 }} />
              
              <Box display="flex" alignItems="center" gap={1}>
                <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Last updated: {lastRefresh.toLocaleTimeString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Recommendations */}
        <Grid item xs={12}>
          <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Performance Recommendations
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#e3f2fd', borderLeft: '4px solid #2196f3' }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                      Memory Optimization
                    </Typography>
                    <Typography variant="body2">
                      Consider increasing memory allocation if usage consistently exceeds 80%. 
                      Current usage: {currentMetrics.memory_usage.toFixed(1)}%
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="warning.main" gutterBottom>
                      CPU Monitoring
                    </Typography>
                    <Typography variant="body2">
                      Review high CPU-consuming processes if spikes are observed. 
                      Current usage: {currentMetrics.cpu_usage.toFixed(1)}%
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#e8f5e8', borderLeft: '4px solid #4caf50' }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="success.main" gutterBottom>
                      Maintenance Schedule
                    </Typography>
                    <Typography variant="body2">
                      Schedule regular maintenance during off-peak hours to ensure optimal performance.
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#fce4ec', borderLeft: '4px solid #e91e63' }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="secondary.main" gutterBottom>
                      Disk Management
                    </Typography>
                    <Typography variant="body2">
                      Monitor disk space regularly and implement cleanup procedures. 
                      Current usage: {currentMetrics.disk_usage.toFixed(1)}%
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SystemMetrics;